// LiveFeed.jsx — upgraded with live reply cards
import React, { useEffect, useRef, useState } from "react";

const EVENT_ICONS = {
  check:        "🔍",
  check_ok:     "✅",
  save:         "💾",
  save_ok:      "✅",
  fetch_start:  "🌐",
  tweet_found:  "📝",
  replies_page: "💬",
  users_fetch:  "👥",
  next_page:    "⏭️",
  rate_limit:   "⏳",
  fetch_ok:     "✅",
  fetch_error:  "❌",
  analyze:      "🤖",
  analyze_ok:   "✅",
  error:        "❌",
  done:         "🎯",
  reply_saved:  "💬",
  geocode:      "🗺️",
  geocode_fail: "🗺️",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=IBM+Plex+Sans+Hebrew:wght@300;400;500;600&display=swap');

  .live-feed * { box-sizing: border-box; direction: rtl; }

  .live-feed {
    background: #0a0c10;
    border: 1px solid #1e2435;
    border-radius: 12px;
    overflow: hidden;
    font-family: 'IBM Plex Sans Hebrew', sans-serif;
    box-shadow: 0 0 40px rgba(59,130,246,0.06);
  }

  /* ── Header ── */
  .lf-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #0d1117;
    border-bottom: 1px solid #1e2435;
  }
  .lf-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .lf-dot.running { background: #3b82f6; box-shadow: 0 0 8px #3b82f6; animation: pulse-dot 1.2s ease-in-out infinite; }
  .lf-dot.done    { background: #22c55e; box-shadow: 0 0 8px #22c55e; }
  .lf-dot.error   { background: #ef4444; box-shadow: 0 0 8px #ef4444; }
  @keyframes pulse-dot {
    0%,100% { opacity: 1; transform: scale(1); }
    50%      { opacity: .5; transform: scale(.7); }
  }
  .lf-title {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px; font-weight: 700;
    letter-spacing: .12em;
  }
  .lf-title.running { color: #3b82f6; }
  .lf-title.done    { color: #22c55e; }
  .lf-title.error   { color: #ef4444; }
  .lf-counter {
    margin-right: auto;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; color: #3f4a60;
  }
  .lf-reply-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    background: rgba(59,130,246,.12);
    color: #3b82f6;
    border: 1px solid rgba(59,130,246,.25);
    padding: 2px 7px; border-radius: 20px;
  }

  /* ── Body ── */
  .lf-body {
    max-height: 380px;
    overflow-y: auto;
    padding: 8px 0;
    scroll-behavior: smooth;
  }
  .lf-body::-webkit-scrollbar { width: 3px; }
  .lf-body::-webkit-scrollbar-track { background: transparent; }
  .lf-body::-webkit-scrollbar-thumb { background: #1e2435; border-radius: 2px; }

  /* ── Log line ── */
  .feed-line {
    display: flex; align-items: center; gap: 8px;
    padding: 3px 14px;
    font-size: 16px; color: #64748b;
    animation: slide-in .15s ease;
  }
  @keyframes slide-in {
    from { opacity: 0; transform: translateX(8px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .feed-line.type-tweet_found  { color: #94a3b8; }
  .feed-line.type-replies_page { color: #3b82f6; }
  .feed-line.type-rate_limit   { color: #f59e0b; }
  .feed-line.type-error,
  .feed-line.type-fetch_error  { color: #ef4444; }
  .feed-line.type-done         { color: #22c55e; font-weight: 600; }
  .feed-line.type-reply_saved  { display: none; } /* handled by cards */

  .feed-line-icon { flex-shrink: 0; font-size: 11px; }
  .feed-line-text { flex: 1; line-height: 1.4; }
  .feed-line-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; border-radius: 10px;
    padding: 1px 6px; flex-shrink: 0;
  }
  .feed-line-badge.replies { background: rgba(59,130,246,.15); color: #3b82f6; }
  .feed-line-badge.users   { background: rgba(34,197,94,.15);  color: #22c55e; }

  /* ── Divider before reply stream ── */
  .lf-divider {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 14px;
    animation: slide-in .2s ease;
  }
  .lf-divider-line { flex: 1; height: 1px; background: #1e2435; }
  .lf-divider-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; letter-spacing: .12em;
    color: #2d3748; white-space: nowrap;
  }

  /* ── Reply Cards Stream ── */
  .lf-replies-stream {
    padding: 2px 10px;
  }

  .reply-card {
    background: #0f1320;
    border: 1px solid #1a2030;
    border-radius: 8px;
    padding: 8px 11px;
    margin-bottom: 5px;
    animation: card-in .22s cubic-bezier(.22,1,.36,1);
    position: relative;
    overflow: hidden;
  }
  @keyframes card-in {
    from { opacity: 0; transform: translateY(-6px) scale(.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .reply-card::before {
    content: '';
    position: absolute; top: 0; right: 0;
    width: 3px; height: 100%;
    background: #1e3a5f;
    border-radius: 0 8px 8px 0;
    transition: background .3s;
  }
  .reply-card.is-bot::before { background: #7f1d1d; }
  .reply-card.is-bot {
    border-color: rgba(239,68,68,.18);
    background: #110d0d;
  }

  .reply-card-top {
    display: flex; align-items: center; gap: 7px;
    margin-bottom: 5px;
  }
  .reply-avatar {
    width: 26px; height: 26px; border-radius: 50%;
    background: #1e2435;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; color: #3f4a60; flex-shrink: 0;
    overflow: hidden;
  }
  .reply-avatar img { width: 100%; height: 100%; object-fit: cover; }

  .reply-meta { flex: 1; min-width: 0; }
  .reply-username {
    font-size: 16px; font-weight: 600; color: #94a3b8;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .reply-location {
    font-size: 10px; color: #3f4a60; margin-top: 1px;
  }

  .bot-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; font-weight: 700;
    letter-spacing: .08em;
    padding: 2px 6px; border-radius: 4px;
    background: rgba(239,68,68,.15);
    color: #ef4444;
    border: 1px solid rgba(239,68,68,.25);
    flex-shrink: 0;
  }
  .new-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; font-weight: 700;
    letter-spacing: .08em;
    padding: 2px 6px; border-radius: 4px;
    background: rgba(59,130,246,.12);
    color: #3b82f6;
    border: 1px solid rgba(59,130,246,.2);
    flex-shrink: 0;
  }

  .reply-text {
    font-size: 16px; color: #64748b;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* ── Summary ── */
  .lf-summary {
    display: flex;
    padding: 10px 14px;
    background: #0d1117;
    border-top: 1px solid #1e2435;
    gap: 16px;
  }
  .sum-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
  .sum-val   { font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 700; line-height: 1; }
  .sum-val.blue   { color: #3b82f6; }
  .sum-val.orange { color: #f59e0b; }
  .sum-val.green  { color: #22c55e; }
  .sum-label {
    font-size: 10px; color: #3f4a60; letter-spacing: .05em;
  }
`;

function FeedLine({ event, msg, data }) {
  if (event === "reply_saved") return null; // rendered as cards
  const icon = EVENT_ICONS[event] || "·";
  const badge = () => {
    if (event === "replies_page" && data?.count)
      return <span className="feed-line-badge replies">{data.count}</span>;
    if (event === "users_fetch" && data)
      return <span className="feed-line-badge users">+{data.new}</span>;
    return null;
  };
  return (
    <div className={`feed-line type-${event}`}>
      <span className="feed-line-icon">{icon}</span>
      <span className="feed-line-text">{msg}</span>
      {badge()}
    </div>
  );
}

function ReplyCard({ reply }) {
  const isBot    = reply.is_bot;
  const isNew    = reply.is_new_user;
  const initials = (reply.username || "?")[0].toUpperCase();

  return (
    <div className={`reply-card ${isBot ? "is-bot" : ""}`}>
      <div className="reply-card-top">
        <div className="reply-avatar">
          {reply.profile_image
            ? <img src={reply.profile_image} alt={reply.username} onError={e => { e.target.style.display = "none"; }} />
            : initials}
        </div>
        <div className="reply-meta">
          <div className="reply-username">@{reply.username || "unknown"}</div>
          {reply.location && <div className="reply-location">📍 {reply.location}</div>}
        </div>
        {isBot  && <span className="bot-tag">BOT</span>}
        {isNew  && !isBot && <span className="new-tag">NEW</span>}
      </div>
      {reply.text && <div className="reply-text">{reply.text}</div>}
    </div>
  );
}

export default function LiveFeed({ username, onDone }) {
  const [lines, setLines]         = useState([]);
  const [replies, setReplies]     = useState([]);
  const [status, setStatus]       = useState("running");
  const [summary, setSummary]     = useState(null);
  const [lineCount, setLineCount] = useState(0);
  const [showDivider, setShowDivider] = useState(false);
  const bodyRef = useRef(null);
  const esRef   = useRef(null);

  const API_FETCH = "https://api.anti-bot.ai";

  useEffect(() => {
    if (!username) return;
    setLines([]); setReplies([]); setStatus("running");
    setSummary(null); setLineCount(0); setShowDivider(false);
    if (esRef.current) esRef.current.close();

    const es = new EventSource(
      `${API_FETCH}/api/targets/add-stream?username=${encodeURIComponent(username)}`
    );
    esRef.current = es;

    es.onmessage = (e) => {
      const { event, msg, data } = JSON.parse(e.data);
      setLineCount(c => c + 1);

      if (event === "reply_saved") {
        setShowDivider(true);
        setReplies(prev => [data, ...prev].slice(0, 50)); // max 50 cards
        return;
      }

      setLines(prev => [...prev, { event, msg, data, id: Date.now() + Math.random() }]);

      if (event === "done") {
        es.close();
        if (msg === "success") {
          setStatus("done");
          setSummary(data);
          if (onDone) setTimeout(() => onDone(data), 800);
        } else {
          setStatus("error");
        }
      }
    };

    es.onerror = () => {
      es.close();
      setStatus("error");
      setLines(prev => [...prev, { event: "error", msg: "שגיאה בחיבור לשרת", id: Date.now() }]);
    };

    return () => es.close();
  }, [username]);

  // Auto-scroll
  useEffect(() => {
    if (bodyRef.current)
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines, replies]);

  const titleText = status === "running" ? "LIVE" : status === "done" ? "DONE" : "ERROR";

  return (
    <>
      <style>{styles}</style>
      <div className="live-feed">

        {/* Header */}
        <div className="lf-header">
          <div className={`lf-dot ${status}`} />
          <span className={`lf-title ${status}`}>{titleText}</span>
          {replies.length > 0 && (
            <span className="lf-reply-badge">{replies.length} תגובות</span>
          )}
          <span className="lf-counter">{lineCount} אירועים</span>
        </div>

        {/* Body */}
        <div className="lf-body" ref={bodyRef}>

          {/* Log lines */}
          {lines.map(line => (
            <FeedLine key={line.id} event={line.event} msg={line.msg} data={line.data} />
          ))}

          {/* Reply cards stream */}
          {showDivider && (
            <>
              <div className="lf-divider">
                <div className="lf-divider-line" />
                <div className="lf-divider-label">LIVE REPLIES</div>
                <div className="lf-divider-line" />
              </div>
              <div className="lf-replies-stream">
                {replies.map((r, i) => (
                  <ReplyCard key={r.twitter_id || i} reply={r} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Summary */}
        {summary && (
          <div className="lf-summary">
            <div className="sum-item">
              <span className="sum-val blue">{summary.replies ?? 0}</span>
              <span className="sum-label">תגובות</span>
            </div>
            <div className="sum-item">
              <span className="sum-val orange">{summary.bots ?? 0}</span>
              <span className="sum-label">בוטים</span>
            </div>
            <div className="sum-item">
              <span className="sum-val green">✓</span>
              <span className="sum-label">הושלם</span>
            </div>
          </div>
        )}

      </div>
    </>
  );
}